import React, { useState } from 'react';
import { Box } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { createConversation, addMessage } from 'apis';
import { NameAndId } from 'types';
import { UploadFileModal } from 'components/Modal';
import {IconTextField} from 'components/IconTextField';
const useStyles = makeStyles(() =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
      margin: '16px 0',
      padding: '0 16px',
      gap: '12px',
    },
    input: {
      width: '100%',
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#C6CBD3', // Hide the default border
        }
      },
    },
    icon: {
      cursor: 'pointer',
    },
  })
);

function checkString(inputString: string) {
  // Check if the string only contains '\n' or is empty
  return /^(\n*|\s*)$/.test(inputString);
}

export const ConversationInputBar: React.FC<{
  roomId: string;
  participants: Array<NameAndId> | null;
  sender: string;
  senderName: string;
}> = React.memo(({ roomId, participants, sender, senderName }) => {
  const classes = useStyles();
  const [content, setContent] = useState<string>('');
  const [open, setOpen] = useState(false);
  const isRoomEmpty = !roomId && participants !== null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Detects Enter button on keyboard
    if (e.key === 'Enter' && e.shiftKey) {
      // add new line
      setContent(content + '\n');
    } else if (e.key === 'Enter') {
      // Create an new room
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (isRoomEmpty) {
      const res = await createConversation(participants);
      await sendMessage(res.id);
    } else {
      await sendMessage(roomId);
    }
  };

  const sendMessage = async (id: string) => {
    if (!checkString(content)) {
      const data = {
        sender,
        senderName,
        content: content,
        type: 'text' as const,
      };
      addMessage(id, data);
      setContent('');
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContent(e.target.value);
  };

  return (
    <Box className={classes.container}>
      <img
        className={classes.icon}
        src={'/file.svg'}
        alt="file"
        onClick={() => setOpen(true)}
      />
      <IconTextField
        icon={<img src={'/send.svg'} alt="send" />}
        onIconClick={handleSubmit}
        value={content}
        multiline
        minRows={1}
        maxRows={2}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
        InputPropsStyle={{
          borderRadius: '10px',
          padding: '8px 16px',
          overflow: 'hidden',
        }}
      />
      {open && (
        <UploadFileModal
          open={open}
          onClose={() => setOpen((prev) => !prev)}
          roomData={{
            isRoomEmpty,
            roomId,
            participants: participants as Array<NameAndId>,
            sender,
            senderName,
          }}
        />
      )}
    </Box>
  );
});

export default React.memo(ConversationInputBar);
